import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import { ReactFCC } from '@notino/react-styleguide';

import { useNewPdDesignEnabled } from '@containers/ProductDetailContainer/ProductDetail/hooks/useNewPdDesignEnabled';
import { TabIds } from '@containers/ProductDetailContainer/ProductDetail/model';
import { useTabsWithExpandableText } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/ProductTabContentProvider';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { useIsDesktop } from '@utils/helpers';

import { useTabSwitcher } from './context/TabSwitcherContext';
import { ITabContentProps } from './model';
import { ChildrenWrapper, TabDivider, TitleWrapper } from './styled';
import { TabTitle } from './TabTitle';
import { trackTab } from './trackingTab';

export const TabContent: ReactFCC<ITabContentProps> = ({
  isDefaultTab,
  children,
  tabId,
  title,
  trackingLabel,
  lazyLoad,
  tabDividerMobile = true,
  tabIdentifier,
  isDefaultTabOpen,
  titleHiddenOnDesktop = false,
}) => {
  const {
    activeTabId,
    isReviewTabVisibleOnMobile,
    toggleIsReviewTabVisibleOnMobile,
  } = useTabSwitcher();
  const { getTimeFromInit } = useTrackingContext();
  const newPdDesignEnabled = useNewPdDesignEnabled();
  const [ref, inView] = useInView({ triggerOnce: true });
  const { toggleTabOpening, tabsWithExpandableTextState } =
    useTabsWithExpandableText();

  const [isOpen, toggleOpen] = React.useReducer(
    (prev) => !prev,
    isDefaultTabOpen ? isDefaultTab : false
  );

  const isDesktop = useIsDesktop();

  const isActive: boolean =
    (typeof activeTabId === 'undefined' && isDefaultTab) ||
    activeTabId === tabId;

  const isExpanded =
    tabId === TabIds.Reviews ? isReviewTabVisibleOnMobile : isOpen;

  const childrenComponent =
    typeof children === 'function'
      ? children({ tabId, activeTabId })
      : children;

  const isTabExpanded = isDesktop ? isActive : isExpanded;

  const handleOpen = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement> | React.KeyboardEvent) => {
      e.stopPropagation();

      const tabWithExpandableText = tabsWithExpandableTextState?.[tabId];

      const isTabOpenForTheFirstTime =
        tabWithExpandableText?.isTabOpenForTheFirstTime;

      const shouldShowFullText = tabWithExpandableText?.shouldShowFullText;

      if (isTabOpenForTheFirstTime) {
        toggleTabOpening(tabId);
      }

      if (trackingLabel) {
        const openOrClose = isTabExpanded ? 'close' : 'open';

        const mode =
          isTabExpanded && isTabOpenForTheFirstTime && !shouldShowFullText
            ? 'close partial'
            : openOrClose;

        trackTab({
          timing: getTimeFromInit(),
          trackingLabel,
          mode,
        });
      }

      if (tabId === TabIds.Reviews) {
        toggleIsReviewTabVisibleOnMobile();
      }

      toggleOpen();
    },
    [
      tabsWithExpandableTextState,
      tabId,
      trackingLabel,
      toggleTabOpening,
      isTabExpanded,
      getTimeFromInit,
      toggleIsReviewTabVisibleOnMobile,
    ]
  );

  return (
    <>
      {title && (
        <>
          <TabDivider
            newPdDesignEnabled={newPdDesignEnabled}
            tabDividerMobile={tabDividerMobile}
            tabDividerDesktop={isActive}
            hideOnDesktop={titleHiddenOnDesktop}
          />
          <TitleWrapper
            isActive={isActive}
            newPdDesignEnabled={newPdDesignEnabled}
            hideOnDesktop={titleHiddenOnDesktop}
          >
            <TabTitle
              isExpanded={isExpanded}
              title={title}
              handleExpand={handleOpen}
              role={isDesktop ? 'heading' : 'button'}
              aria-controls={tabIdentifier}
              aria-expanded={isExpanded}
              tabIndex={isDesktop ? -1 : 0}
              newPdDesignEnabled={newPdDesignEnabled}
            />
          </TitleWrapper>
        </>
      )}
      <div
        role="tabpanel"
        tabIndex={isTabExpanded ? 0 : -1}
        aria-hidden={!isTabExpanded}
        id={tabIdentifier}
      >
        <ChildrenWrapper
          ref={ref}
          isExpanded={isExpanded}
          isActive={isActive}
          newPdDesignEnabled={newPdDesignEnabled}
        >
          {lazyLoad ? inView && childrenComponent : childrenComponent}
        </ChildrenWrapper>
        <TabDivider
          tabDividerMobile={true}
          newPdDesignEnabled={newPdDesignEnabled}
        />
      </div>
    </>
  );
};
